import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase/config.js';
import { doc, getDoc, collection, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Container, Row, Col, Table, Spinner, Alert, Button, Modal, Form } from 'react-bootstrap';
import InstructorClassesTable from './InstructorClassesTable.js';
import InstructorButtons from './InstructorButtons.js'
import { RolesDragdown } from '../ui/RolesDragdown.js';
import '../App.css';

export default function InstructorDashboard() {
  const [userName, setUserName] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const todayDate = new Date().toISOString().split('T')[0];

  const getUser = async (uid) => {
    try {
      const userDocRef = doc(db, "users", uid);
      const userSnapshot = await getDoc(userDocRef);
      if (userSnapshot.exists()) {
        setUserName(`${userSnapshot.data().lastName}`);
      } else {
        setError("User profile not found");
      }
    } catch (err) {
      setError("Failed to load user data");
    }
  };

  const getAppointments = async () => {
    try {
      const submissionsRef = collection(db, "retakeSubmissions");
      const snapshot = await getDocs(submissionsRef);
      const appointmentsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          time: data.time || "N/A"
        };
      });
      console.log("Appointments Data:", appointmentsData); // Debug line
      setAppointments(appointmentsData);
    } catch (err) {
      setError("Failed to load appointments");
    }
  };

  const getClasses = async (uid) => {
    try {
      const classQuery = query(collection(db, "classes"), where("instructorId", "==", uid));
      const snapshot = await getDocs(classQuery);
      setClasses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setError("Failed to load classes");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await Promise.all([getUser(user.uid), getAppointments(), getClasses(user.uid)]);
        } catch (err) {
          setError("Failed to load dashboard data");
        } finally {
          setLoading(false);
        }
      } else {
        setError("No user logged in");
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSaveAppointment = async () => {
    if (!selectedAppointment) return;
    const appointmentRef = doc(db, "retakeSubmissions", selectedAppointment.id);
    try {
      await updateDoc(appointmentRef, {
        time: selectedAppointment.time,
        tokens: selectedAppointment.tokens || 0,
        date: selectedAppointment.date,
      });
      setShowEditModal(false);
      getAppointments();
    } catch (err) {
      console.error("Error updating appointment:", err);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Alert variant="danger" className="text-center">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100">
      <Container className="dashboard-container w-75 shadow p-4">
        <Row className="align-items-center mb-4">
          <Col><h1 className="dashboard-heading text-center">Hello, {userName ? `Dr.${userName}` : 'Instructor'}!</h1></Col>
          <Col xs="auto"><RolesDragdown /></Col>
        </Row>

        <Row>
          <Col lg={12} className="mb-4">
            <h2 className="section-heading text-center">Appointments</h2>
            <Table striped bordered hover className="mt-3">
              <thead className="table-dark">
              <tr>
                <th className="dark-green-header">Student Name</th>
                <th className="dark-green-header">Course Name</th>
                <th className="dark-green-header">Email</th>
                <th className="dark-green-header">Quiz</th>
                <th className="dark-green-header">Tokens</th>
                <th className="dark-green-header">Date</th>
                <th className="dark-green-header">Time</th>
                <th className="dark-green-header">Actions</th>
              </tr>
              </thead>
              <tbody>
              {appointments.length > 0 ? appointments.map((appointment) => (
                <tr key={appointment.id}>
                  <td>{appointment.studentName || 'N/A'}</td>
                  <td>{appointment.courseNum || 'N/A'}</td>
                  <td>{appointment.studentEmail || 'N/A'}</td>
                  <td>{appointment.quiz || 'N/A'}</td> {/* quiz field display */}
                  <td>{appointment.tokens || '0'}</td>
                  <td>{appointment.date}</td>
                  <td>{appointment.time || 'N/A'}</td>
                  <td>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setShowEditModal(true);
                      }}
                    >Edit</Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="text-center text-muted">No appointments scheduled</td>
                </tr>
              )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <h2 className="section-heading text-center">Your Classes</h2>
            {classes.length > 0 ? (
              <InstructorClassesTable classes={classes} />
            ) : (
              <Alert variant="success" className="text-center">No classes assigned yet</Alert>
            )}
          </Col>
        </Row>

        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Body>
            <Form>
              <Form.Group className="mt-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={selectedAppointment?.date || ''}
                  onChange={(e) =>
                    setSelectedAppointment({
                      ...selectedAppointment,
                      date: e.target.value,
                    })
                  }
                  min={todayDate}
                  required
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>Time</Form.Label>
                <Form.Select
                  value={selectedAppointment?.time || ''}
                  onChange={(e) =>
                    setSelectedAppointment({
                      ...selectedAppointment,
                      time: e.target.value,
                    })
                  }
                >
                  <option value="">Select Time</option>
                  {/* time options... */}
                  {["7:30 AM","8:00 AM","8:30 AM","9:00 AM","9:30 AM","10:00 AM","10:30 AM","11:00 AM","11:30 AM","12:00 PM",
                    "12:30 PM","1:00 PM","1:30 PM","2:00 PM","2:30 PM","3:00 PM","3:30 PM","4:00 PM","4:30 PM","5:00 PM",
                    "5:30 PM","6:00 PM","6:30 PM","7:00 PM","7:30 PM","8:00 PM","8:30 PM","9:00 PM"].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>Tokens</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={selectedAppointment?.tokens || 0}
                  onChange={(e) =>
                    setSelectedAppointment({
                      ...selectedAppointment,
                      tokens: parseInt(e.target.value, 10),
                    })
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveAppointment}>Save Changes</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
